import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, CheckCircle, XCircle, Info, ShoppingCart, Eye } from 'lucide-react';
import axios from 'axios';
import './ProductComparison.css';
import { getImageUrl } from '../../utils/imageUtils';

const ProductComparison = ({ productIds, productsData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  

 const baseURL = window.APP_CONFIG?.API_URL || "https://backendweb.properteck.lk";

  useEffect(() => {
    fetchProducts();
  }, [productIds, location.state]);

  const fetchProducts = async () => {
    setIsLoading(true);
    setApiError(null);
    
    try {
      if (location.state?.products && location.state.products.length >= 2) {
        const formattedProducts = location.state.products.map(product => ({
          ...product,
          features: product.features || (Array.isArray(product.description) ? product.description : [product.description || 'No features available']),
          description: Array.isArray(product.description) ? product.description.join(', ') : (product.description || `${product.name} - ${product.brand}`),
          rating: product.rating || 4.0,
          reviews: product.reviews || 0
        }));
        
        setProducts(formattedProducts);
        setIsLoading(false);
        return;
      }

      const navigationState = typeof window !== 'undefined' && window.history.state?.usr;
      if (navigationState?.products && navigationState.products.length >= 2) {
        const formattedProducts = navigationState.products.map(product => ({
          ...product,
          features: product.features || (Array.isArray(product.description) ? product.description : [product.description || 'No features available']),
          description: Array.isArray(product.description) ? product.description.join(', ') : (product.description || `${product.name} - ${product.brand}`),
          rating: product.rating || 4.0,
          reviews: product.reviews || 0
        }));
        
        setProducts(formattedProducts);
        setIsLoading(false);
        return;
      }

      if (productIds && productIds.length >= 2) {
        const productPromises = productIds.map(id => 
          axios.get(`${baseURL}/products/getproduct/${id}`, { timeout: 5000 })
        );
        
        const responses = await Promise.all(productPromises);
        const fetchedProducts = responses.map(response => {
          const product = response.data.product || response.data;
          return {
            ...product,
            features: product.features || (Array.isArray(product.description) ? product.description : [product.description || 'No features available']),
            rating: product.rating || 4.0,
            reviews: product.reviews || 0,
            description: Array.isArray(product.description) ? product.description.join(', ') : (product.description || `${product.name} - ${product.brand}`)
          };
        });
        
        setProducts(fetchedProducts);
        setIsLoading(false);
        return;
      }

      const response = await axios.get(`${baseURL}/products/getproducts`, { timeout: 5000 });
      const allProducts = response.data.products || response.data || [];
      
      if (!Array.isArray(allProducts) || allProducts.length < 2) {
        throw new Error('Not enough products available for comparison');
      }

      const firstTwoProducts = allProducts.slice(0, 2).map(product => ({
        ...product,
        features: product.features || (Array.isArray(product.description) ? product.description : [product.description || 'No features available']),
        rating: product.rating || 4.0,
        reviews: product.reviews || 0,
        description: Array.isArray(product.description) ? product.description.join(', ') : (product.description || `${product.name} - ${product.brand}`)
      }));

      setProducts(firstTwoProducts);
      
    } catch (error) {
      console.error('Error fetching products:', error);
      setApiError({
        message: 'Failed to load products for comparison',
        details: error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryConnection = () => {
    fetchProducts();
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  // Using utility function from imageUtils

  const renderProductPrice = (product) => {
    if (product.type === 'special_offer') {
      return (
        <div className="productcomp-price-container">
          <div className="productcomp-price-row">
            <span className="productcomp-offer-price">Rs.{product.offerPrice}</span>
            <span className="productcomp-discount-badge">
              {product.discount}% OFF
            </span>
          </div>
          <span className="productcomp-regular-price">Rs.{product.regularPrice}</span>
        </div>
      );
    } else if (product.type === 'new_arrival') {
      return (
        <div className="productcomp-price-container">
          <span className="productcomp-offer-price">Rs.{product.offerPrice}</span>
          <span className="productcomp-regular-price">Rs.{product.regularPrice}</span>
        </div>
      );
    } else {
      return <span className="productcomp-normal-price">Rs.{product.price}</span>;
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="productcomp-star productcomp-filled" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" className="productcomp-star productcomp-half" />);
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="productcomp-star productcomp-empty" />);
    }

    return stars;
  };

  const ComparisonRow = ({ label, values, type = 'text' }) => {
    const getBetterValue = () => {
      if (type === 'price') {
        return values.indexOf(Math.min(...values.map(v => parseFloat(v.replace(/[^\d.]/g, '')))));
      }
      return -1;
    };

    const betterIndex = getBetterValue();

    return (
      <div className="productcomp-comparison-row">
        <div className="productcomp-comparison-label">
          {label}
          {type === 'price' && <Info className="productcomp-info-icon" />}
        </div>
        {values.map((value, index) => (
          <div
            key={index}
            className={`productcomp-comparison-value ${betterIndex === index ? 'productcomp-better-value' : ''}`}
          >
            {value}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="productcomp-container">
      <div className="productcomp-wrapper">
        <div className="productcomp-hero-section">
          <h1 className="productcomp-title">Product Comparison</h1>
          <p className="productcomp-subtitle">Compare features and specifications side by side</p>
          
          <div className="productcomp-header">
            <button onClick={handleBackClick} className="productcomp-back-btn">
              <ArrowLeft className="productcomp-icon" />
              Back to Products
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="productcomp-loading-message">
            <div className="productcomp-loading-spinner"></div>
            <h3>Loading comparison...</h3>
            <p>Please wait while we prepare your product comparison</p>
          </div>
        ) : apiError ? (
          <div className="productcomp-error-message">
            <XCircle className="productcomp-error-icon" />
            <h3>Comparison Error</h3>
            <p>{apiError.message}</p>
            <p>{apiError.details}</p>
            <button className="productcomp-retry-btn" onClick={handleRetryConnection}>
              Retry Connection
            </button>
          </div>
        ) : products.length < 2 ? (
          <div className="productcomp-error-message">
            <XCircle className="productcomp-error-icon" />
            <h3>Insufficient Products</h3>
            <p>Unable to load products for comparison</p>
            <p>At least 2 products are required for comparison</p>
            <button className="productcomp-retry-btn" onClick={handleBackClick}>
              Go Back
            </button>
          </div>
        ) : (
          <>
            <div className="productcomp-product-grid">
              {products.map((product) => (
                <div key={product._id} className="productcomp-card">
                  <div className="productcomp-image-container">
                    <img
                      src={getImageUrl(product.imageUrl)}
                      alt={product.name}
                      className="productcomp-image"
                    />
                    <div className="productcomp-image-overlay">
                      <button className="productcomp-view-btn">
                        <Eye className="productcomp-icon" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="productcomp-info">
                    <h3 className="productcomp-name">{product.name}</h3>
                    <p className="productcomp-brand">{product.brand}</p>
                    
                    <div className="productcomp-rating">
                      <div className="productcomp-stars">
                        {renderStars(product.rating)}
                      </div>
                      <span className="productcomp-rating-text">
                        {product.rating} ({product.reviews} reviews)
                      </span>
                    </div>
                    
                    {renderProductPrice(product)}
                    
                    <div className="productcomp-card-actions">
                      <button className="productcomp-add-to-cart-btn">
                        <ShoppingCart className="productcomp-icon" />
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="productcomp-tabs">
              <div className="productcomp-tabs-nav">
                {[
                  { id: 'overview', label: 'Overview' },
                  { id: 'features', label: 'Features' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`productcomp-tab-button ${activeTab === tab.id ? 'productcomp-active' : ''}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="productcomp-tab-content">
                {activeTab === 'overview' && (
                  <div className="productcomp-overview-content">
                    <ComparisonRow
                      label="Product Name"
                      values={products.map(p => p.name)}
                    />
                    <ComparisonRow
                      label="Brand"
                      values={products.map(p => p.brand)}
                    />
                    <ComparisonRow
                      label="Price"
                      values={products.map(p => 
                        p.type === 'special_offer' || p.type === 'new_arrival' 
                          ? `Rs.${p.offerPrice}` 
                          : `Rs.${p.price}`
                      )}
                      type="price"
                    />
                    <ComparisonRow
                      label="Description"
                      values={products.map(p => p.description)}
                    />
                  </div>
                )}

                {activeTab === 'features' && (
                  <div className="productcomp-features-content">
                    {products.map((product) => (
                      <div key={product._id} className="productcomp-feature-column">
                        <h4 className="productcomp-feature-title">{product.name}</h4>
                        <ul className="productcomp-feature-list">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="productcomp-feature-item">
                              <CheckCircle className="productcomp-check-icon" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="productcomp-cta">
              <h2>Need help choosing?</h2>
              <p>Contact our experts for personalized product recommendations and assistance.</p>
              <div className="productcomp-cta-button-container">
                <button className="productcomp-contact-btn" onClick={() => navigate('/contact')}>
                  Contact Us
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductComparison;